/**
 * Подмножество markdown Discord для текста сообщений: **жирный**, *курсив*, __подчёркнутый__,
 * ~~зачёркнутый~~, ||спойлер||, `код`, ```блок кода```, ссылки (<ссылка> — без превью),
 * [текст](ссылка), <@id>; в начале строки — # заголовки, -# подпись и > цитаты (markdownBlocks.ts).
 * Никакого innerHTML: результат — дерево React-элементов.
 */
import { Fragment, type MouseEvent, type ReactNode } from 'react'
import styles from './markdown.module.css'
import { Spoiler } from '@/components/chat/Spoiler'
import { CodeBlock } from '@/components/chat/CodeBlock'
import { cx } from './cx'
import { misleadingLabel, parseBlocks, type Block } from './markdownBlocks'

const TOKEN = /(```([\s\S]*?)```)|(`([^`\n]+)`)|(\*\*([^*]+)\*\*)|(~~([^~]+)~~)|(\|\|([^|]+)\|\|)|(\*([^*\n]+)\*)|(_([^_\n]+)_)|(<@&(\d+)>)|(<@!?(\d+)>)|(https?:\/\/[^\s<>)]+)|(@(everyone|here)\b)|(<#(\d+)>)|(<(https?:\/\/[^\s<>]+)>)|(<(a?):([A-Za-z0-9_]{2,32}):(\d+)>)|(\*\*\*([^*]+)\*\*\*)|(__([\s\S]+?)__(?!_))|(\[([^[\]\n]+)\]\((?:<(https?:\/\/[^\s<>]+)>|(https?:\/\/(?:[^\s()<>]|\([^\s()<>]*\))+))\))/g

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

/** Адрес картинки кастомного эмодзи по id. */
/** Картинка стикера: PNG/APNG отдаются как .png, GIF — как .gif (GET /stickers/{id}.{ext}). */
export function stickerImageUrl(s: { id: string; format_type: number }): string {
  return `${API_BASE}/stickers/${s.id}.${s.format_type === 4 ? 'gif' : 'png'}`
}

export function emojiImageUrl(id: string, animated = false): string {
  return `${API_BASE}/emojis/${id}.${animated ? 'gif' : 'png'}`
}

/** Текст состоит только из эмодзи (Unicode или кастомных) — показываем крупно. */
export function isEmojiOnly(text: string): boolean {
  const stripped = text.replace(/<a?:[A-Za-z0-9_]{2,32}:\d+>/g, '').replace(/(\p{Extended_Pictographic}|\p{Emoji_Modifier}|\u200d|\ufe0f|\u20e3|\s)/gu, '')
  return stripped.length === 0 && text.trim().length > 0 && text.trim().length <= 120
}

export interface RenderOptions {
  /** Имя пользователя по id для упоминаний. */
  userName?: (id: string) => string | undefined
  /** Роль по id: имя и цвет для <@&id>. */
  role?: (id: string) => { name: string; color?: string | undefined } | undefined
  /** Канал по id для <#id>; onClick — переход в канал. */
  channel?: (id: string) => { name: string; onClick?: (() => void) | undefined } | undefined
  /** Клик по упоминанию пользователя: открыть карточку профиля. */
  onUser?: ((id: string, e: MouseEvent<HTMLElement>) => void) | undefined
  /** Сообщение из одних эмодзи — крупные картинки. */
  jumbo?: boolean
  /** Строка-превью (ответ, закреплённые, поиск): заголовки и цитаты — без блоков, в одну строку. */
  inline?: boolean
}

export function renderMarkdown(text: string, opts: RenderOptions = {}): ReactNode[] {
  const blocks = parseBlocks(text)
  // без заголовков и цитат сообщение размечается ровно как раньше
  if (blocks.length === 1 && blocks[0]?.kind === 'text') return renderInline(text, opts)
  return renderBlocks(blocks, opts)
}

function renderBlocks(blocks: Block[], opts: RenderOptions): ReactNode[] {
  return blocks.map((b, i) => {
    // в превью блоки идут подряд в одну строку, и разделяет их пробел вместо переноса
    const gap = opts.inline && i > 0 ? ' ' : null
    switch (b.kind) {
      case 'text':
        return <Fragment key={i}>{gap}{renderInline(b.text, opts)}</Fragment>
      case 'heading': {
        if (opts.inline) return <Fragment key={i}>{gap}<span className={styles.inlineHeading}>{renderInline(b.text, opts)}</span></Fragment>
        const Heading = b.level === 1 ? 'h1' : b.level === 2 ? 'h2' : 'h3'
        return <Heading key={i} className={styles.heading}>{renderInline(b.text, opts)}</Heading>
      }
      case 'subtext':
        return opts.inline ? <Fragment key={i}>{gap}<span className={styles.inlineSubtext}>{renderInline(b.text, opts)}</span></Fragment> : <small key={i} className={styles.subtext}>{renderInline(b.text, opts)}</small>
      case 'quote': {
        const inner = renderBlocks(parseBlocks(b.text, false), opts)
        return opts.inline ? <Fragment key={i}>{gap}{inner}</Fragment> : <blockquote key={i} className={styles.quote}>{inner}</blockquote>
      }
    }
  })
}

/** Строчная разметка: жирный, ссылки, упоминания, код. Внутри неё блоков уже нет. */
function renderInline(text: string, opts: RenderOptions): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let key = 0
  for (const m of text.matchAll(TOKEN)) {
    const start = m.index ?? 0
    if (start > last) out.push(text.slice(last, start))
    if (m[1] !== undefined) {
      const code = m[2]?.replace(/^\n/, '') ?? ''
      // в однострочном превью кнопке «Копировать» не место — там блок как был
      out.push(opts.inline ? <pre key={key++} className={styles.block}><code>{code}</code></pre> : <CodeBlock key={key++} text={code} className={styles.block} />)
    } else if (m[3] !== undefined) out.push(<code key={key++} className={styles.code}>{m[4]}</code>)
    else if (m[5] !== undefined) out.push(<strong key={key++}>{renderInline(m[6] ?? '', opts)}</strong>)
    else if (m[7] !== undefined) out.push(<s key={key++}>{renderInline(m[8] ?? '', opts)}</s>)
    else if (m[9] !== undefined) out.push(<Spoiler key={key++}>{renderInline(m[10] ?? '', opts)}</Spoiler>)
    else if (m[11] !== undefined) out.push(<em key={key++}>{renderInline(m[12] ?? '', opts)}</em>)
    else if (m[13] !== undefined) out.push(<em key={key++}>{renderInline(m[14] ?? '', opts)}</em>)
    else if (m[15] !== undefined) {
      const role = opts.role?.(m[16] ?? '')
      const style = role?.color ? { color: role.color, backgroundColor: `${role.color}33` } : undefined
      out.push(<span key={key++} className={styles.mention} style={style}>@{role?.name ?? 'удалённая роль'}</span>)
    } else if (m[17] !== undefined) {
      const id = m[18] ?? ''
      const label = `@${opts.userName?.(id) ?? id}`
      out.push(
        opts.onUser ? (
          <button key={key++} type="button" className={styles.mention} onClick={(e) => opts.onUser?.(id, e)} onContextMenu={(e) => opts.onUser?.(id, e)}>{label}</button>
        ) : (
          <span key={key++} className={styles.mention}>{label}</span>
        ),
      )
    } else if (m[19] !== undefined) {
      const url = m[19]
      out.push(<a key={key++} href={url} target="_blank" rel="noreferrer noopener" className={styles.link}>{url}</a>)
    } else if (m[20] !== undefined) {
      out.push(<span key={key++} className={styles.mention}>@{m[21]}</span>)
    } else if (m[24] !== undefined) {
      const url = m[25] ?? ''
      out.push(<a key={key++} href={url} target="_blank" rel="noreferrer noopener" className={styles.link}>{url}</a>)
    } else if (m[26] !== undefined) {
      const animated = m[27] === 'a'
      const name = m[28] ?? ''
      out.push(<img key={key++} className={cx(styles.emoji, opts.jumbo && styles.emojiJumbo)} src={emojiImageUrl(m[29] ?? '', animated)} alt={`:${name}:`} data-tooltip={`:${name}:`} draggable={false} />)
    } else if (m[22] !== undefined) {
      const ch = opts.channel?.(m[23] ?? '')
      out.push(
        ch?.onClick ? (
          <button key={key++} type="button" className={styles.mention} onClick={ch.onClick}>#{ch.name}</button>
        ) : (
          <span key={key++} className={styles.mention}>#{ch?.name ?? 'удалённый-канал'}</span>
        ),
      )
    } else if (m[30] !== undefined) {
      out.push(<strong key={key++}><em>{renderInline(m[31] ?? '', opts)}</em></strong>)
    } else if (m[32] !== undefined) {
      out.push(<u key={key++}>{renderInline(m[33] ?? '', opts)}</u>)
    } else if (m[34] !== undefined) {
      const url = m[36] ?? m[37] ?? ''
      const label = m[35] ?? ''
      // Текст-адрес, ведущий на другой сервер, — приманка: показываем, куда ссылка ведёт на самом деле.
      // Ссылки, спойлеры и упоминания внутри ссылки дали бы кнопку или ссылку в ссылке — такой текст без разметки.
      const shown = misleadingLabel(label, url) ? url : /https?:\/\/|\|\||<[@#]/.test(label) ? label : renderInline(label, opts)
      out.push(<a key={key++} href={url} data-tooltip={url} target="_blank" rel="noreferrer noopener" className={styles.link}>{shown}</a>)
    }
    last = start + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}
